/** ContentSettings -- content strategy & LinkedIn poster. All plans (Starter: 1 automated post/week; Pro: daily). PUT /api/settings/content. */

import { useState, useCallback, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Loader2, CheckCircle2, Sparkles, Wand2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ChipInput } from '@/components/chip-input'
import { useApi } from '@/hooks/use-api'
import { usePlan } from '@/hooks/use-plan'
import type { ContentConfig } from '@dealcloser/shared'
import { DEFAULT_CONTENT } from '@dealcloser/shared'

type Option = { readonly value: string; readonly label: string }

const TONE_OPTIONS: Option[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'educational', label: 'Educational' },
  { value: 'provocative', label: 'Provocative' },
]
const CTA_MODE_OPTIONS: Option[] = [
  { value: 'subtle', label: 'Subtle' },
  { value: 'direct', label: 'Direct' },
  { value: 'none', label: 'None' },
]
const POSTER_FREQ_OPTIONS: Option[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'every_other_day', label: 'Every other day' },
  { value: 'weekly', label: 'Weekly' },
]

function SelectField({ label, value, options, onChange, disabled }: {
  label: string; value: string; options: Option[]; onChange: (v: string) => void; disabled: boolean
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  )
}

const contentKeys = { config: ['settings', 'content'] as const }

export default function ContentSettings() {
  const plan = usePlan()
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  const [form, setForm] = useState<ContentConfig>(DEFAULT_CONTENT)
  const [savedSuccess, setSavedSuccess] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  const [previewFeedback, setPreviewFeedback] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: contentKeys.config,
    queryFn: async (): Promise<ContentConfig> => {
      const res = await apiFetch<ContentConfig>('/api/settings/content')
      if (!res.success || !res.data) return DEFAULT_CONTENT
      return res.data
    },
    staleTime: 5 * 60_000,
  })

  useEffect(() => { if (data) setForm(data) }, [data])

  const saveMutation = useMutation({
    mutationFn: async (config: ContentConfig): Promise<void> => {
      const res = await apiFetch('/api/settings/content', { method: 'PUT', body: config })
      if (!res.success) throw new Error(res.error ?? 'Failed to save')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.config })
      setSavedSuccess(true)
      setTimeout(() => setSavedSuccess(false), 3000)
    },
  })

  // Sample generation for the setup flow: see what the automation would
  // write, react with feedback, and only then switch it on. Previews never
  // save a post or use the weekly allowance.
  const previewMutation = useMutation({
    mutationFn: async (styleNotes: string | undefined): Promise<string> => {
      const res = await apiFetch<{ content: string }>('/api/content/generate-preview', {
        method: 'POST',
        body: { ...(styleNotes ? { style_notes: styleNotes } : {}) },
      })
      if (!res.success || !res.data?.content) {
        throw new Error(res.error ?? 'Preview failed. Try again in a moment.')
      }
      return res.data.content
    },
    onSuccess: (content) => setPreview(content),
  })

  function handlePreview() {
    previewMutation.mutate(form.posterStyleNotes?.trim() || undefined)
  }

  function handleFeedbackRegenerate() {
    const feedback = previewFeedback.trim()
    if (!feedback) return
    // Feedback becomes a standing style note so every future post follows it.
    const merged = [form.posterStyleNotes?.trim(), feedback].filter(Boolean).join('\n').slice(0, 1000)
    setForm((prev) => ({ ...prev, posterStyleNotes: merged }))
    setPreviewFeedback('')
    previewMutation.mutate(merged)
  }

  const setTopics = useCallback((contentTopics: readonly string[]) => {
    setForm((prev) => ({ ...prev, contentTopics }))
  }, [])
  const setPillars = useCallback((contentPillars: readonly string[]) => {
    setForm((prev) => ({ ...prev, contentPillars }))
  }, [])

  const set = <K extends keyof ContentConfig>(key: K) => (v: ContentConfig[K]) =>
    setForm((prev) => ({ ...prev, [key]: v }))

  const isSaving = saveMutation.isPending
  const saveError = saveMutation.error instanceof Error ? saveMutation.error.message : null
  const disabled = isLoading || isSaving

  // Basic gets 1 automated post per week, so the cadence picker collapses to
  // weekly; Pro can go daily. The orchestrator clamps server-side too.
  const posterFreqOptions = plan === 'starter'
    ? POSTER_FREQ_OPTIONS.filter((o) => o.value === 'weekly')
    : POSTER_FREQ_OPTIONS

  return (
    <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Content Strategy</CardTitle>
            <CardDescription>Define topics, tone, and posting cadence for LinkedIn content.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <ChipInput label="Content Topics" placeholder="Add topic..." chips={form.contentTopics} onChange={setTopics} disabled={disabled} />
            <SelectField label="Content Tone" value={form.contentTone} options={TONE_OPTIONS} onChange={set('contentTone')} disabled={disabled} />
            {/* "Posting Frequency" removed 2026-09-26: nothing read it. How
                often posts go out is the Poster Frequency below. */}
            <ChipInput label="Content Pillars (3-5 themes)" placeholder="Add pillar..." chips={form.contentPillars} onChange={setPillars} disabled={disabled} />
            <SelectField label="Content CTA Mode" value={form.contentCtaMode} options={CTA_MODE_OPTIONS} onChange={set('contentCtaMode')} disabled={disabled} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>LinkedIn Poster</CardTitle>
            <CardDescription>
              {plan === 'starter'
                ? 'The autopilot writes 1 LinkedIn post for you each week, rotating through your topics.'
                : 'The autopilot writes a LinkedIn post for you every day, rotating through your topics.'}
            </CardDescription>
            <p className="text-xs text-muted-foreground">
              Want a specific topic and style on set days (for example Mon, Wed, Fri at 9 AM)? Use{' '}
              <Link to="/content?new=auto" className="text-cobalt-bright hover:underline">auto-post campaigns</Link> on the
              Content page: you see a sample post first, then pick the schedule. The poster and auto-post campaigns
              share your plan&apos;s weekly allowance of automated posts ({plan === 'starter' ? '1' : '7'} per week).
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-2">
              <Checkbox id="poster-enabled" checked={form.posterEnabled} onCheckedChange={(c) => setForm((prev) => ({ ...prev, posterEnabled: c === true }))} disabled={disabled} />
              <label htmlFor="poster-enabled" className="text-sm cursor-pointer">Enable automated LinkedIn posting</label>
            </div>
            {form.posterEnabled && (
              <>
                <SelectField label="Poster Frequency" value={plan === 'starter' ? 'weekly' : form.posterFrequency} options={posterFreqOptions} onChange={set('posterFrequency')} disabled={disabled} />
                {plan === 'starter' && (
                  <p className="text-xs text-muted-foreground">
                    The Basic plan includes 1 automated post per week. Upgrade to Pro for a fresh post every day.
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <Checkbox id="poster-auto-approve" checked={form.posterAutoApprove} onCheckedChange={(c) => setForm((prev) => ({ ...prev, posterAutoApprove: c === true }))} disabled={disabled} />
                  <label htmlFor="poster-auto-approve" className="text-sm cursor-pointer">Auto-approve generated posts (skip review)</label>
                </div>
                <p className="text-xs text-muted-foreground -mt-3">
                  Off: each post waits as a draft for your approval. On: posts go out automatically at 11:00 AM ET.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="poster-style-notes">Style notes for the AI (optional)</Label>
                  <textarea
                    id="poster-style-notes"
                    className="w-full resize-none rounded-md border bg-transparent p-2 text-sm outline-none"
                    rows={3}
                    maxLength={1000}
                    placeholder="e.g. short punchy sentences, always include a client story, never mention pricing"
                    value={form.posterStyleNotes ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, posterStyleNotes: e.target.value }))}
                    disabled={disabled}
                  />
                </div>

                {/* Preview-and-feedback loop: see a sample before switching on. */}
                <div className="space-y-3 rounded-lg border border-dashed p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">See what it would write</p>
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={handlePreview} disabled={previewMutation.isPending || disabled}>
                      {previewMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                      {preview ? 'New sample' : 'Preview a sample post'}
                    </Button>
                  </div>
                  {previewMutation.error instanceof Error && (
                    <p className="text-xs text-destructive">{previewMutation.error.message}</p>
                  )}
                  {preview && (
                    <>
                      <div className="rounded-md border bg-muted/30 p-3">
                        <p className="whitespace-pre-wrap text-sm leading-relaxed">{preview}</p>
                      </div>
                      <div className="flex gap-2">
                        <input
                          className="h-8 flex-1 rounded-md border bg-transparent px-2 text-sm outline-none"
                          maxLength={300}
                          placeholder="Feedback, e.g. shorter, more casual, lead with a question"
                          value={previewFeedback}
                          onChange={(e) => setPreviewFeedback(e.target.value)}
                          disabled={previewMutation.isPending}
                        />
                        <Button size="sm" className="gap-1.5" onClick={handleFeedbackRegenerate} disabled={previewMutation.isPending || !previewFeedback.trim()}>
                          <Wand2 className="h-3.5 w-3.5" />
                          Apply &amp; regenerate
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Feedback is added to your style notes so every future post follows it. Save your settings to keep it.
                      </p>
                    </>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center gap-3">
          <Button onClick={() => saveMutation.mutate(plan === 'starter' ? { ...form, posterFrequency: 'weekly' } : form)} disabled={disabled}>
            {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Save Content Settings'}
          </Button>
          {savedSuccess && (
            <div className="flex items-center gap-1.5 text-sm text-emerald-300 ">
              <CheckCircle2 className="h-4 w-4" />Saved successfully
            </div>
          )}
          {saveError && <p className="text-sm text-destructive">{saveError}</p>}
        </div>
    </div>
  )
}
